import { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, FieldArray, useFormikContext } from "formik";
import { motion } from "framer-motion";
import { ClipboardList, Sparkles, Loader2, Search } from "lucide-react";

import { createTask } from "../../api/tasks";
import { fetchCustomers } from "../../api/users";
import { getAllTaskTemplates, getTaskTemplate } from "../../api/taskTemplates";

import ProInput from "../form/Input";
import ProSelect from "../form/Select";
import ProDatePicker from "../form/DatePicker";

/* =========================
   PREFILL HELPER
   ========================= */
function PrefillFormikFields({ prefillCustomer, prefillTractor, customers }) {
  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    if (!prefillCustomer || customers.length === 0) return;

    setFieldValue("customerId", prefillCustomer.id);

    if (prefillTractor) {
      setFieldValue("tractorId", prefillTractor.id);
    }
  }, [prefillCustomer, prefillTractor, customers, setFieldValue]);

  return null;
}

/* =========================
   TEMPLATE AUTOFILL BRIDGE
   ========================= */
function TemplateAutofillBridge({
  createFromTemplate,
  selectedTemplate,
  selectedTemplateId,
}) {
  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    if (!createFromTemplate) return;
    if (!selectedTemplateId || !selectedTemplate) return;

    const orderedItems = [...(selectedTemplate.checklistItems || [])]
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map((item) => item.description)
      .filter(Boolean);

    setFieldValue("title", selectedTemplate.name || "");
    setFieldValue("description", selectedTemplate.description || "");
    setFieldValue("checklist", orderedItems.length > 0 ? orderedItems : [""]);
  }, [
    createFromTemplate,
    selectedTemplate,
    selectedTemplateId,
    setFieldValue,
  ]);

  return null;
}

export default function AddTaskModal({
  open,
  onClose,
  prefillCustomer,
  prefillTractor,
  initialTemplate = null,
  forceTemplateMode = false,
}) {
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const [taskTemplates, setTaskTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingTemplateDetails, setLoadingTemplateDetails] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [createFromTemplate, setCreateFromTemplate] = useState(false);
  const [templateQuery, setTemplateQuery] = useState("");

  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;

    let active = true;
    setLoadingCustomers(true);

    fetchCustomers()
      .then((data) => {
        if (!active) return;

        setCustomers(
          (data || []).map((c) => ({
            id: c.id,
            name: `${c.firstName} ${c.lastName}`,
            tractors: c.tractors || [],
          }))
        );
      })
      .catch((err) => {
        console.error("FETCH CUSTOMERS FAILED", err);
        if (active) setCustomers([]);
      })
      .finally(() => {
        if (active) setLoadingCustomers(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    let active = true;
    setLoadingTemplates(true);
    setTemplateError("");

    getAllTaskTemplates({ activeOnly: true })
      .then((response) => {
        if (!active) return;

        const normalized = Array.isArray(response)
          ? response
          : response?.data || [];

        setTaskTemplates(normalized);
      })
      .catch((err) => {
        console.error("FETCH TEMPLATES FAILED", err);
        if (active) {
          setTaskTemplates([]);
          setTemplateError("Failed to load task templates.");
        }
      })
      .finally(() => {
        if (active) setLoadingTemplates(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    setFormError("");
    setTemplateError("");

    if (initialTemplate) {
      const seededTemplate = initialTemplate?.data || initialTemplate;

      setCreateFromTemplate(true);
      setSelectedTemplate(seededTemplate);
      setSelectedTemplateId(
        seededTemplate?.id ? String(seededTemplate.id) : ""
      );
      setTemplateQuery(seededTemplate?.name || "");
      return;
    }

    setSelectedTemplateId("");
    setSelectedTemplate(null);
    setTemplateQuery("");
    setCreateFromTemplate(Boolean(forceTemplateMode));
  }, [open, initialTemplate, forceTemplateMode]);

  useEffect(() => {
    if (!open) return;
    if (!createFromTemplate) return;
    if (!selectedTemplateId) {
      setSelectedTemplate(null);
      return;
    }

    const seededTemplateId = initialTemplate?.id ?? initialTemplate?.data?.id;
    if (seededTemplateId && String(seededTemplateId) === String(selectedTemplateId)) {
      return;
    }

    let active = true;
    setLoadingTemplateDetails(true);
    setTemplateError("");

    getTaskTemplate(selectedTemplateId)
      .then((data) => {
        if (!active) return;
        setSelectedTemplate(data?.data || data);
      })
      .catch((err) => {
        console.error("FETCH TEMPLATE DETAILS FAILED", err);
        if (active) {
          setSelectedTemplate(null);
          setTemplateError("Failed to load the selected template.");
        }
      })
      .finally(() => {
        if (active) setLoadingTemplateDetails(false);
      });

    return () => {
      active = false;
    };
  }, [open, createFromTemplate, selectedTemplateId, initialTemplate]);

  const filteredTemplates = useMemo(() => {
    const q = templateQuery.trim().toLowerCase();

    if (!q) return taskTemplates || [];

    return (taskTemplates || []).filter((template) => {
      const haystack = [
        template.name,
        template.description,
        ...(template.checklistItems || []).map((item) => item.description),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [taskTemplates, templateQuery]);

  const templateOptions = useMemo(() => {
    return filteredTemplates.map((template) => ({
      label: template.name,
      value: template.id,
    }));
  }, [filteredTemplates]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm">
      <div className="flex min-h-full items-start justify-center px-4 py-6 sm:py-10">
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 12 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="flex max-h-[calc(100vh-3rem)] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="shrink-0 flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Create New Task
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Create and assign a service task for a customer and tractor.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-xl leading-none text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              ×
            </button>
          </div>

          <Formik
            enableReinitialize
            initialValues={{
              title: "",
              description: "",
              customerId: prefillCustomer?.id || "",
              tractorId: prefillTractor?.id || "",
              assignedDate: "",
              dueDate: "",
              estimatedHours: "",
              checklist: [""],
            }}
            onSubmit={async (values, { resetForm, setSubmitting }) => {
              setFormError("");

              try {
                const payload = {
                  title: values.title,
                  description: values.description,
                  technicianId: null,
                  customerId: Number(values.customerId),
                  tractorId: Number(values.tractorId),
                  assignedDate: new Date(values.assignedDate)
                    .toISOString()
                    .slice(0, 19),
                  dueDate: new Date(values.dueDate)
                    .toISOString()
                    .slice(0, 19),
                  estimatedHours: Number(values.estimatedHours),
                  status: "PENDING_ACCEPTANCE",
                  location: {
                    latitude: -1.286389,
                    longitude: 36.817223,
                    name: "Nairobi CBD",
                  },
                  checklist: values.checklist
                    .filter(Boolean)
                    .map((text) => ({
                      text,
                      completed: false,
                    })),
                };

                await createTask(payload);
                resetForm();
                setSelectedTemplateId("");
                setSelectedTemplate(null);
                setCreateFromTemplate(false);
                setTemplateQuery("");
                onClose?.();
              } catch (err) {
                console.error("CREATE TASK ERROR", err);
                setFormError("Failed to create task.");
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, isSubmitting }) => {
              const selectedCustomer = customers.find(
                (c) => c.id === Number(values.customerId)
              );

              const tractors = selectedCustomer?.tractors || [];

              const customerOptions = customers.map((c) => ({
                label: c.name,
                value: c.id,
              }));

              const tractorOptions = tractors.map((t) => ({
                label: `${t.model} (${t.serial})`,
                value: t.id,
              }));

              const isTemplateLocked = !!initialTemplate;
              const canToggleTemplateMode = !forceTemplateMode && !isTemplateLocked;

              return (
                <Form className="flex min-h-0 flex-1 flex-col">
                  <PrefillFormikFields
                    prefillCustomer={prefillCustomer}
                    prefillTractor={prefillTractor}
                    customers={customers}
                  />

                  <TemplateAutofillBridge
                    createFromTemplate={createFromTemplate}
                    selectedTemplate={selectedTemplate}
                    selectedTemplateId={selectedTemplateId}
                  />

                  <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                    <div className="space-y-6">
                      {formError && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                          {formError}
                        </div>
                      )}

                      <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <div className="mb-5 flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <Sparkles className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                              <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                                Template Starter
                              </h3>
                            </div>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                              Start from a reusable template and auto-populate task
                              details and checklist steps.
                            </p>
                          </div>

                          <label className="inline-flex cursor-pointer items-center gap-3">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              Create from template
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                if (!canToggleTemplateMode) return;

                                const next = !createFromTemplate;
                                setCreateFromTemplate(next);

                                if (!next) {
                                  setSelectedTemplateId("");
                                  setSelectedTemplate(null);
                                  setTemplateQuery("");
                                }
                              }}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                                createFromTemplate
                                  ? "bg-slate-900 dark:bg-white"
                                  : "bg-slate-300 dark:bg-slate-700"
                              } ${
                                !canToggleTemplateMode
                                  ? "cursor-not-allowed opacity-70"
                                  : ""
                              }`}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white transition dark:bg-slate-900 ${
                                  createFromTemplate
                                    ? "translate-x-5"
                                    : "translate-x-1"
                                }`}
                              />
                            </button>
                          </label>
                        </div>

                        {createFromTemplate && (
                          <div className="space-y-4">
                            {templateError && (
                              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                                {templateError}
                              </div>
                            )}

                            <div className="space-y-4">
                              <div className="relative">
                                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <input
                                  type="text"
                                  value={templateQuery}
                                  onChange={(e) => setTemplateQuery(e.target.value)}
                                  placeholder="Search templates by name, description, or checklist item"
                                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-slate-600"
                                  disabled={isTemplateLocked}
                                />
                              </div>

                              <div className="grid grid-cols-1 gap-5 md:grid-cols-[1fr_auto]">
                                <div>
                                  <Field
                                    name="taskTemplatePicker"
                                    label="Task Template"
                                    icon="📚"
                                    component={ProSelect}
                                    value={selectedTemplateId}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setSelectedTemplateId(value);
                                    }}
                                    options={templateOptions}
                                    placeholder={
                                      loadingTemplates
                                        ? "Loading templates..."
                                        : filteredTemplates.length === 0
                                        ? "No matching templates found"
                                        : "Select active template"
                                    }
                                    disabled={
                                      loadingTemplates ||
                                      filteredTemplates.length === 0 ||
                                      isTemplateLocked
                                    }
                                  />
                                </div>

                                {loadingTemplateDetails && (
                                  <div className="flex items-end">
                                    <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                      Applying template...
                                    </div>
                                  </div>
                                )}
                              </div>

                              {!loadingTemplates &&
                                templateQuery.trim() &&
                                filteredTemplates.length === 0 && (
                                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950/30 dark:text-slate-400">
                                    No templates match{" "}
                                    <span className="font-medium">
                                      "{templateQuery}"
                                    </span>
                                    .
                                  </div>
                                )}
                            </div>

                            {selectedTemplate && !loadingTemplateDetails && (
                              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4 dark:border-slate-800 dark:bg-slate-950/30">
                                <div className="flex items-start gap-3">
                                  <div className="mt-0.5 rounded-xl bg-slate-100 p-2 dark:bg-slate-800">
                                    <ClipboardList className="h-4 w-4 text-slate-700 dark:text-slate-300" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                      {selectedTemplate.name}
                                    </p>
                                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                      {selectedTemplate.description ||
                                        "No template description provided."}
                                    </p>
                                    <p className="mt-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                                      {(selectedTemplate.checklistItems || []).length} checklist item
                                      {(selectedTemplate.checklistItems || []).length === 1
                                        ? ""
                                        : "s"}{" "}
                                      will be copied into this task draft.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </section>

                      <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-800 dark:bg-slate-950/30">
                        <div className="mb-5">
                          <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                            Task Details
                          </h3>
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Define the task title, description, and timing.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 gap-5">
                          <Field
                            name="title"
                            label="Task Title"
                            icon="📌"
                            component={ProInput}
                          />

                          <Field
                            name="description"
                            label="Task Description"
                            icon="📝"
                            component={ProInput}
                            textarea
                          />

                          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                            <Field
                              name="assignedDate"
                              label="Assigned Date"
                              icon="📅"
                              component={ProDatePicker}
                            />

                            <Field
                              name="dueDate"
                              label="Due Date"
                              icon="⏳"
                              component={ProDatePicker}
                            />
                          </div>

                          <Field
                            name="estimatedHours"
                            label="Estimated Hours"
                            type="number"
                            icon="⏱"
                            component={ProInput}
                          />
                        </div>
                      </section>

                      <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <div className="mb-5">
                          <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                            Assignment Context
                          </h3>
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Select the customer and the relevant tractor.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                          <Field
                            name="customerId"
                            label="Customer"
                            icon="👤"
                            component={ProSelect}
                            disabled={!!prefillCustomer || loadingCustomers}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFieldValue("customerId", value);
                              setFieldValue("tractorId", "");
                            }}
                            options={customerOptions}
                            placeholder={
                              loadingCustomers
                                ? "Loading customers..."
                                : "Select customer"
                            }
                          />

                          <Field
                            name="tractorId"
                            label="Tractor"
                            icon="🚜"
                            component={ProSelect}
                            disabled={!!prefillTractor || !values.customerId}
                            options={tractorOptions}
                            placeholder={
                              values.customerId
                                ? "Select tractor"
                                : "Select customer first"
                            }
                          />
                        </div>
                      </section>

                      <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <div className="mb-5 flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                              Checklist
                            </h3>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                              Break the task into clear execution steps.
                            </p>
                          </div>

                          <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {values.checklist.filter(Boolean).length} steps
                          </div>
                        </div>

                        <FieldArray
                          name="checklist"
                          render={(arrayHelpers) => (
                            <div className="space-y-3">
                              {values.checklist.map((_, i) => (
                                <div
                                  key={i}
                                  className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-950/30"
                                >
                                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                    {i + 1}
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <Field
                                      name={`checklist.${i}`}
                                      component={ProInput}
                                      placeholder={`Step ${i + 1}`}
                                      icon="✔️"
                                    />
                                  </div>

                                  {values.checklist.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => arrayHelpers.remove(i)}
                                      className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-red-600 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-red-400"
                                      aria-label={`Remove step ${i + 1}`}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ))}

                              <button
                                type="button"
                                onClick={() => arrayHelpers.push("")}
                                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                              >
                                <span className="text-base leading-none">＋</span>
                                Add Step
                              </button>
                            </div>
                          )}
                        />
                      </section>
                    </div>
                  </div>

                  <div className="shrink-0 flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-6 py-4 sm:flex-row sm:justify-end dark:border-slate-800 dark:bg-slate-900">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-black"
                    >
                      {isSubmitting && (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent dark:border-black/70 dark:border-t-transparent" />
                      )}
                      {isSubmitting ? "Saving..." : "Save Task"}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </motion.div>
      </div>
    </div>
  );
}